import type { Id } from '@hq/utils';

/** 与后端 Hq.Shared/Contracts/ApiResult.cs 的 ApiCode 一一对应。 */
export const ApiCode = {
  Ok: 0,
  ParamInvalid: 40001,
  Unauthorized: 40100,
  Forbidden: 40300,
  NotFound: 40400,
  IdempotentConflict: 40900,
  BizRuleViolated: 42200,
  RateLimited: 42900,
  ServerError: 50000
} as const;

export type ApiCodeValue = (typeof ApiCode)[keyof typeof ApiCode];

/** 统一响应体：`{ code, message, data, traceId }`；code !== 0 即失败。 */
export interface ApiEnvelope<T> {
  code: number;
  message: string;
  data: T;
  traceId?: string | null;
}

/**
 * 游标分页（后端 PageQuery，DR-4.4）：**没有 total**，只能 lastId 向后翻页。
 * 时间筛选传 ISO 时间，主键区间换算在服务端完成。
 */
export interface PageQuery {
  lastId?: Id | null;
  /** 默认 20，后端限制 (0, 200]，越界回落 20。 */
  size?: number;
  beginTime?: string;
  endTime?: string;
}

export interface PageResult<T> {
  items: T[];
  nextLastId: Id | null;
  hasMore: boolean;
}

/** 资源服务（Hq.Res.Api）的裸错误体：`{ code: <HTTP 状态码>, message }`。 */
export interface ResourceErrorBody {
  code: number;
  message: string;
}

const HTTP_STATUS_TO_CODE: Record<number, number> = {
  400: ApiCode.ParamInvalid,
  401: ApiCode.Unauthorized,
  403: ApiCode.Forbidden,
  404: ApiCode.NotFound,
  409: ApiCode.IdempotentConflict,
  413: ApiCode.ParamInvalid,
  415: ApiCode.ParamInvalid,
  422: ApiCode.BizRuleViolated,
  // 限流：后端 ApiCode.RateLimited，原先缺失会被归一成 ServerError（提示「服务异常」而非「操作过于频繁」）
  429: ApiCode.RateLimited
};

/** HTTP 状态 → 业务码（后端 ApiExceptionMiddleware 的反向映射）。 */
export function httpStatusToCode(status: number): number {
  return HTTP_STATUS_TO_CODE[status] ?? ApiCode.ServerError;
}

export interface ApiErrorInit {
  code: number;
  message: string;
  httpStatus?: number;
  traceId?: string | null;
  payload?: unknown;
}

/** 归一后的接口异常：Web / RN / 小程序共用同一套判定。 */
export class ApiError extends Error {
  readonly code: number;
  readonly httpStatus: number;
  readonly traceId: string | null;
  readonly payload: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.httpStatus = init.httpStatus ?? 200;
    this.traceId = init.traceId ?? null;
    this.payload = init.payload;
  }

  get isUnauthorized(): boolean {
    return this.code === ApiCode.Unauthorized;
  }

  get isForbidden(): boolean {
    return this.code === ApiCode.Forbidden;
  }

  /** 幂等冲突：同一 X-Request-Id 重复提交，提示“已提交，请刷新”。 */
  get isIdempotentConflict(): boolean {
    return this.code === ApiCode.IdempotentConflict;
  }

  /** 业务规则不满足：直接展示后端 message。 */
  get isBizRuleViolated(): boolean {
    return this.code === ApiCode.BizRuleViolated;
  }

  /** 可直接展示给用户的文案（docs/05 第 6 章第 9 条）。 */
  get friendlyMessage(): string {
    if (this.isIdempotentConflict) return '该请求已提交，请刷新后查看结果';
    if (this.code === ApiCode.BizRuleViolated) return this.message || '业务校验未通过';
    if (this.isUnauthorized) return '登录状态已失效，请重新登录';
    if (this.isForbidden) return '没有操作权限';
    return this.message || '服务暂时不可用，请稍后重试';
  }
}
